import type { ErrorSchema } from '@rjsf/utils';
import { decodeAssetID } from 'features/assets/assets.util';
import { type AssetSearchResult, useGetAssetsByIdsQuery } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const ASSET_ID_PREFIX = 'JourneysAssetId-';

type UseAssetManagementProps = {
  value: string;
  onChange: (value: unknown, es?: ErrorSchema<unknown> | undefined, id?: string) => void;
  requiredWidth?: number;
  requiredHeight?: number;
};

export const useAssetManagement = ({ value, onChange, requiredWidth, requiredHeight }: UseAssetManagementProps) => {
  const initialAssetId = value?.startsWith(ASSET_ID_PREFIX) ? decodeAssetID(value) : '';
  const [currentAsset, setCurrentAsset] = useState<AssetSearchResult | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [assetIds, setAssetIds] = useState<string[]>([
    initialAssetId,
  ]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Get route params
  const { trafficProjectId } = useParams<{
    trafficProjectId: string;
  }>();

  // Asset data fetching
  const { data: assetData, loading } = useGetAssetsByIdsQuery({
    skip: !assetIds.length || !assetIds[0],
    variables: {
      input: {
        assetIds,
        projectId: trafficProjectId,
      },
    },
  });

  // Handlers - memoized to prevent unnecessary re-renders
  const handleOpenModal = useCallback(() => setOpenModal(true), []);
  const handleCloseModal = useCallback(() => setOpenModal(false), []);
  const handleRemoveImage = useCallback(() => {
    onChange('');
    setCurrentAsset(null);
    setAssetIds([]);
    setValidationError(null);
  }, [
    onChange,
  ]);

  const handleOnAssetsReady = useCallback(
    (assets: AssetSearchResult[]) => {
      if (!assets.length) return;

      const selectedAsset = assets[0];
      const { width, height } = selectedAsset.assetMetadata ?? {};

      if (requiredWidth !== undefined && requiredHeight !== undefined) {
        if (width === undefined || height === undefined) {
          setValidationError('Unable to validate image dimensions. Asset metadata is missing.');
          setOpenModal(false);
          return;
        }
        if (width !== requiredWidth || height !== requiredHeight) {
          setValidationError(`Image must be ${requiredWidth}x${requiredHeight} pixels. Selected image is ${width}x${height} pixels.`);
          setOpenModal(false);
          return;
        }
      }

      // Clear any previous validation errors
      setValidationError(null);
      const newAssetIds = assets.map((asset) => asset.assetId);
      setAssetIds(newAssetIds);
      onChange(`${ASSET_ID_PREFIX}${newAssetIds[0]}`);
      setOpenModal(false);
    },
    [
      onChange,
      requiredWidth,
      requiredHeight,
    ],
  );

  // Effect to update current asset when data is fetched
  useEffect(() => {
    const assets = assetData?.getAssetsByIds?.assets;
    if (assets && assets.length > 0) {
      setCurrentAsset(assets[0] as AssetSearchResult);
    }
  }, [
    assetData?.getAssetsByIds?.assets,
  ]);

  return {
    currentAsset,
    handleCloseModal,
    handleOnAssetsReady,
    handleOpenModal,
    handleRemoveImage,
    loading,
    openModal,
    trafficProjectId,
    validationError,
  };
};
